import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Check, BadgeCheck, Users, Star, Wallet, FileText, ServerCog, Ticket, ShieldAlert, Mail, Globe, Phone, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { GuideHero, GoldButton, GhostButton, Reveal, FAQ, GuideCTA } from "../components/GuideBits";
import { PREREG } from "../data/hajjGuides";
import { useContent } from "../context/ContentContext";
import { sendInquiry } from "../lib/api";

const benefitIcons = [Users, Star, BadgeCheck];
const stepIcons = [Wallet, FileText, ServerCog, Ticket];
const guideIcons = [ShieldAlert, Mail, Globe];

const FeeCard = ({ c }) => (
  <motion.div initial={{ opacity: 0, y: 40, rotate: -2 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ duration: 0.8, delay: 0.35 }} className="relative" data-testid="prereg-fee-card">
    <div className="absolute -inset-3 rounded-3xl bg-jannah-gold/20 blur-2xl" />
    <div className="relative rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 p-8 text-white shadow-2xl">
      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-jannah-gold-light">
        <span className="w-2 h-2 rounded-full bg-jannah-gold animate-pulse" /> {c.open_badge}
      </span>
      <div className="mt-6 text-sm text-white/70">{c.fee_label}</div>
      <div className="mt-1 flex items-end gap-2">
        <span className="text-5xl md:text-6xl font-bold text-jannah-gold leading-none">৳ 30,000</span>
        <span className="text-white/70 pb-1">/ {c.fee_per}</span>
      </div>
      <ul className="mt-7 space-y-3">
        {c.fee_points.map((p, i) => (
          <motion.li key={i} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 + i * 0.15 }} className="flex items-center gap-3 text-sm">
            <span className="w-6 h-6 rounded-full bg-jannah-gold/20 border border-jannah-gold/40 flex items-center justify-center"><Check size={13} className="text-jannah-gold-light" /></span> {p}
          </motion.li>
        ))}
      </ul>
    </div>
  </motion.div>
);

const PreRegForm = ({ c, lang }) => {
  const f = c.form;
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", type: f.types[0], idno: "", pilgrims: "1", district: "", comments: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "w-full px-4 py-3 rounded-lg border border-jannah-sand bg-white text-jannah-ink focus:outline-none focus:ring-2 focus:ring-jannah-green/40 transition-shadow";
  const label = "block text-sm font-medium text-jannah-ink mb-1.5";

  const submit = (e) => {
    e.preventDefault();
    sendInquiry("prereg", { ...form, lang }).catch(() => {});
    toast.success(f.success_title, { description: f.success_sub });
    setDone(true);
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl border border-jannah-sand p-12 text-center shadow-xl" data-testid="prereg-success">
        <CheckCircle2 className="mx-auto text-jannah-green mb-5" size={64} />
        <h3 className="text-3xl font-bold text-jannah-ink">{f.success_title}</h3>
        <p className="mt-3 text-jannah-ink/70">{f.success_sub}</p>
        <button onClick={() => { setDone(false); setForm({ ...form, name: "", idno: "", comments: "" }); }} className="mt-8 px-6 py-3 rounded-md border-2 border-jannah-green text-jannah-green font-semibold hover:bg-jannah-green/10" data-testid="prereg-another-btn">{f.another}</button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-jannah-sand p-8 md:p-10 shadow-xl space-y-5" data-testid="prereg-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><label className={label}>{f.name} *</label><input required value={form.name} onChange={set("name")} className={input} data-testid="prereg-name" /></div>
        <div><label className={label}>{f.phone} *</label><input required type="tel" value={form.phone} onChange={set("phone")} className={input} placeholder="01XXXXXXXXX" data-testid="prereg-phone" /></div>
        <div><label className={label}>{f.email} *</label><input required type="email" value={form.email} onChange={set("email")} className={input} data-testid="prereg-email" /></div>
        <div>
          <label className={label}>{f.type} *</label>
          <select value={form.type} onChange={set("type")} className={input} data-testid="prereg-type">
            {f.types.map((tp) => <option key={tp}>{tp}</option>)}
          </select>
        </div>
        <div><label className={label}>{f.idno}</label><input value={form.idno} onChange={set("idno")} className={input} data-testid="prereg-idno" /></div>
        <div className="grid grid-cols-2 gap-5">
          <div><label className={label}>{f.pilgrims}</label><input type="number" min="1" value={form.pilgrims} onChange={set("pilgrims")} className={input} data-testid="prereg-pilgrims" /></div>
          <div><label className={label}>{f.district}</label><input value={form.district} onChange={set("district")} className={input} data-testid="prereg-district" /></div>
        </div>
      </div>
      <div><label className={label}>{f.comments}</label><textarea rows={4} value={form.comments} onChange={set("comments")} className={input} data-testid="prereg-comments" /></div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
        <span className="text-xs text-muted-foreground">{f.required}</span>
        <button type="submit" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full btn-gold font-bold uppercase tracking-wider text-sm" data-testid="prereg-submit">
          <Send size={16} /> {f.submit}
        </button>
      </div>
    </form>
  );
};

const HajjPreRegistration = () => {
  const { lang } = useLang();
  const { CONTACT, IMAGES } = useContent();
  const c = PREREG[lang];
  const scrollToForm = () => document.getElementById("prereg-form-section")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div data-testid="prereg-page">
      <GuideHero
        testid="prereg-hero"
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={c.subtitle}
        image={IMAGES.heroSlides[0]}
        primary={<GoldButton onClick={scrollToForm} testid="prereg-hero-cta">{c.cta_primary} <ArrowRight size={17} /></GoldButton>}
        secondary={<GhostButton href={CONTACT.whatsapp} testid="prereg-hero-whatsapp"><MessageCircle size={17} /> {c.cta_secondary}</GhostButton>}
        aside={<FeeCard c={c} />}
      />

      {/* Benefits */}
      <section className="section-pad bg-white" data-testid="prereg-benefits">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="max-w-2xl">
            <span className="eyebrow text-jannah-green">{c.benefits_eyebrow}</span>
            <div className="gold-divider mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-jannah-navy">{c.benefits_title}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {c.benefits.map((b, i) => {
              const Icon = benefitIcons[i];
              return (
                <Reveal key={i} i={i}>
                  <div className="group h-full rounded-3xl border border-jannah-sand bg-white shadow-ambient p-8 hover:bg-white hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300">
                    <div className="w-14 h-14 rounded-2xl bg-jannah-green text-white flex items-center justify-center group-hover:bg-jannah-gold transition-colors"><Icon size={26} /></div>
                    <h3 className="mt-6 text-2xl font-bold text-jannah-ink">{b.title}</h3>
                    <p className="mt-3 text-jannah-ink/70 leading-relaxed">{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-pad mesh-band relative overflow-hidden" data-testid="prereg-process">
        <div className="absolute inset-0 pattern-overlay opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="eyebrow text-jannah-gold-light">{c.process_eyebrow}</span>
            <div className="gold-divider mx-auto mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-white">{c.process_title}</h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-4 gap-6 relative">
            <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, ease: "easeOut" }} className="hidden md:block absolute top-9 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-jannah-gold/30 via-jannah-gold to-jannah-gold/30 origin-left" />
            {c.steps.map((s, i) => {
              const Icon = stepIcons[i];
              return (
                <Reveal key={i} i={i + 1} className="relative text-center">
                  <div className="mx-auto w-[72px] h-[72px] rounded-full bg-jannah-green-dark border-2 border-jannah-gold flex items-center justify-center relative z-10 shadow-[0_0_30px_rgba(200,170,0,0.35)]">
                    <Icon size={28} className="text-jannah-gold-light" />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full btn-gold text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  </div>
                  <h3 className="mt-6 text-xl font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-white/70 text-sm leading-relaxed">{s.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="section-pad bg-jannah-cream" data-testid="prereg-docs">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="eyebrow text-jannah-green">{c.docs_eyebrow}</span>
            <div className="gold-divider mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-jannah-navy">{c.docs_title}</h2>
            <div className="mt-6 inline-flex items-start gap-3 rounded-xl bg-jannah-gold/10 border border-jannah-gold/40 p-4 text-jannah-ink/80 text-sm">
              <Star size={18} className="text-jannah-gold shrink-0 mt-0.5" /> {c.docs_note}
            </div>
            <div className="mt-8 relative rounded-3xl overflow-hidden shadow-2xl hidden lg:block">
              <img src={IMAGES.aboutSecondary} alt="" className="w-full h-72 object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-jannah-green-dark/70 to-transparent" />
            </div>
          </Reveal>
          <Reveal i={1}>
            <Tabs defaultValue="adult" className="bg-white rounded-2xl border border-jannah-sand shadow-xl p-6 md:p-8">
              <TabsList className="grid grid-cols-3 h-auto bg-jannah-cream p-1.5 rounded-xl">
                {c.doc_tabs.map((tb) => (
                  <TabsTrigger key={tb.key} value={tb.key} className="rounded-lg py-2.5 text-xs md:text-sm font-semibold data-[state=active]:bg-jannah-green data-[state=active]:text-white data-[state=active]:shadow" data-testid={`prereg-tab-${tb.key}`}>{tb.label}</TabsTrigger>
                ))}
              </TabsList>
              {c.doc_tabs.map((tb) => (
                <TabsContent key={tb.key} value={tb.key} className="mt-6">
                  <ul className="space-y-3">
                    {tb.items.map((it, i) => (
                      <motion.li key={it} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }} className="flex items-center gap-4 rounded-xl border border-jannah-sand p-4 hover:border-jannah-green/40 hover:bg-jannah-green/5 transition-colors">
                        <span className="w-9 h-9 rounded-full bg-jannah-green/10 flex items-center justify-center shrink-0"><Check size={17} className="text-jannah-green" /></span>
                        <span className="text-jannah-ink font-medium">{it}</span>
                      </motion.li>
                    ))}
                  </ul>
                  <div className="mt-6 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{c.fee_label}</span>
                    <span className="font-bold text-jannah-green text-lg">৳ 30,000</span>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </Reveal>
        </div>
      </section>

      {/* Guidelines */}
      <section className="section-pad bg-white" data-testid="prereg-guidelines">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="eyebrow text-jannah-green">{c.guide_eyebrow}</span>
            <div className="gold-divider mx-auto mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-jannah-navy">{c.guide_title}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {c.guidelines.map((g, i) => {
              const Icon = guideIcons[i];
              return (
                <Reveal key={i} i={i}>
                  <div className="h-full rounded-2xl border-l-4 border-jannah-gold bg-jannah-cream/70 p-7 hover:shadow-lg transition-shadow">
                    <Icon size={26} className="text-jannah-green" />
                    <h3 className="mt-4 text-xl font-bold text-jannah-ink">{g.title}</h3>
                    <p className="mt-2 text-jannah-ink/70 text-sm leading-relaxed">{g.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="prereg-form-section" className="section-pad bg-jannah-cream scroll-mt-32" data-testid="prereg-form-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-12">
          <Reveal className="lg:col-span-2">
            <span className="eyebrow text-jannah-green">{c.form_eyebrow}</span>
            <div className="gold-divider mt-3" />
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-jannah-navy">{c.form_title}</h2>
            <p className="mt-5 text-jannah-ink/70 leading-relaxed">{c.form_sub}</p>
            <div className="mt-10 rounded-2xl bg-jannah-green-dark text-white p-7 relative overflow-hidden">
              <div className="absolute inset-0 pattern-overlay opacity-30" />
              <div className="relative">
                <h3 className="text-xl font-bold text-jannah-gold">{c.side_title}</h3>
                <p className="mt-2 text-white/75 text-sm">{c.side_sub}</p>
                <div className="mt-5 space-y-3 text-sm">
                  <a href={`tel:${CONTACT.phone2}`} className="flex items-center gap-3 hover:text-jannah-gold-light"><Phone size={16} className="text-jannah-gold" /> {CONTACT.person} · {CONTACT.phone}</a>
                  <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-jannah-gold-light"><MessageCircle size={16} className="text-jannah-gold" /> WhatsApp</a>
                  <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-jannah-gold-light"><Mail size={16} className="text-jannah-gold" /> {CONTACT.email}</a>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal i={1} className="lg:col-span-3">
            <PreRegForm c={c} lang={lang} />
          </Reveal>
        </div>
      </section>

      <FAQ eyebrow={c.faq_eyebrow} title={c.faq_title} items={c.faqs} testid="prereg-faq" />
      <GuideCTA title={c.cta_title} sub={c.cta_sub} btnLabel={c.cta_btn} btnTo="/hajj" image={IMAGES.arafat} testid="prereg-cta" />
    </div>
  );
};

export default HajjPreRegistration;
